/** * お知らせコンポーネント */
<template>
  <div class="information">
    <loading-mask v-bind:isLoading="isLoadingInformationList">
      <div class="information-wrapper">
        <div class="title-bar">
          <h1 v-t="'appInformation.information'" />
          <div class="title-bar-action">
            <b-form-select
              class="information-period-select"
              v-model="days"
              v-bind:options="options"
            />
          </div>
        </div>
        <div class="information-body">
          <ul class="information-list">
            <li
              class="information-item"
              v-for="info in informationsForDisplay"
              v-bind:info="info"
              v-bind:key="info.id"
            >
              <a v-on:click="$_showModal(info.id)">
                <div class="information-title">
                  {{ info.title }}
                </div>
                <div class="information-date">
                  {{ info.disp_at }}
                </div>
              </a>
            </li>
          </ul>
          <b-pagination
            v-model="currentPage"
            v-bind:total-rows="totalRows"
            v-bind:per-page="limit"
            align="center"
            size="sm"
            class="information-pagination"
          />
        </div>
      </div>
    </loading-mask>

    <ifield-b-modal
      id="information-dialog"
      ref="information-dialog-modal"
      v-bind:title="$t('appInformation.message.informationFromIField')"
      centered
      scrollable
      size="lg"
    >
      <!-- body部 -->
      <loading-mask v-bind:isLoading="isLoadingInformation">
        <article class="dialog-body">
          <section class="dialog-body-header">
            <div class="dialog-logo" />
            <div
              class="diglog-content-date"
              v-html="informationForDisplay.disp_at"
            />
          </section>
          <section class="dialog-body-content">
            <h3 class="dialog-content-title">
              {{ informationForDisplay.title }}
            </h3>
            <p
              class="dialog-content-text"
              v-html="informationForDisplay.content"
            />
          </section>
        </article>
      </loading-mask>
      <!-- footer部 -->
      <template v-slot:modal-footer="{ cancel }">
        <div class="dialog-footer">
          <div class="dialog-footer-left">
            <a
              href="https://www.multisoup.co.jp/contact/"
              target="_blank"
              v-t="'appInformation.message.contact'"
            />
          </div>
          <div class="dialog-footer-right">
            <b-button
              size="sm"
              v-bind:disabled="isLastInformation"
              v-bind:variant="cancelButtonVariant"
              v-t="'appInformation.action.prev'"
              v-on:click="$_turnPreviousInformationDetail"
            />
            <b-button
              size="sm"
              v-bind:disabled="isFirstInformation"
              v-bind:variant="okButtonVariant"
              v-t="'appInformation.action.next'"
              v-on:click="$_turnNextInformationDetail"
            />
            <b-button
              size="sm"
              variant="secondary"
              v-t="'appInformation.action.close'"
              v-on:click="cancel"
            />
          </div>
        </div>
      </template>
    </ifield-b-modal>
  </div>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import moment from 'moment'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { toLocalTz } from '@/js/TimezoneHelper'
import StringUtil from '@/js/util/StringUtil'
import {
  DATE_FORMAT_HYPHEN,
  DATE_FORMAT_SLASH,
  TIME_FORMAT_SECOND,
} from '@/constants'

/**
 * DateオブジェクトをYYYY-MM-DD形式の文字列に変換する
 * @param {date} date 日付
 * @return {string} 日付文字列(YYYY-MM-DD)
 */
function toYyyyMmDd(date) {
  return moment(date).format(DATE_FORMAT_HYPHEN)
}

export default {
  name: 'AppInformation',
  props: {
    // 1ページに表示するお知らせ数
    limit: {
      type: Number,
      default: 10,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      informations: [],
      information: {
        id: '',
        title: '',
        content: '',
        disp_at: '',
      },
      days: 31, // 表示日数
      period: {
        from: '',
        to: '',
      }, // お知らせ表示期間
      totalRows: 0, // おしらせの総件数
      currentPage: 1, // カレントページ番号
      options: [
        { value: 7, text: this.$t('appInformation.period.1week') },
        { value: 31, text: this.$t('appInformation.period.1month') },
        { value: 92, text: this.$t('appInformation.period.3months') },
        { value: 184, text: this.$t('appInformation.period.6months') },
        { value: -1, text: this.$t('appInformation.period.all') },
      ],
      // お知らせリスト取得中フラグ
      isLoadingInformationList: false,
      // 単体のお知らせ取得中フラグ
      isLoadingInformation: false,
    }
  },
  computed: {
    // お知らせのID配列
    informationIds: function () {
      return this.informations.map((info) => info.id)
    },
    /**
     * 表示用お知らせリストデータ
     * @type {Object[]}
     */
    informationsForDisplay: function () {
      return this.informations.map((d) => ({
        ...d,
        disp_at: moment(d.disp_at).format(DATE_FORMAT_SLASH),
      }))
    },
    /**
     * 表示用お知らせ詳細データ
     * @type {Object}
     */
    informationForDisplay: function () {
      if (!this.information) {
        return {
          id: '',
          title: '',
          content: '',
          disp_at: '',
        }
      }
      const { content, disp_at } = this.information
      const dispAtMoment = moment(disp_at)
      const date = dispAtMoment.format(DATE_FORMAT_SLASH)
      const time = dispAtMoment.format(TIME_FORMAT_SECOND)
      return {
        ...this.information,
        // v-html で出力するため、HTML エスケープしてから改行を <br /> に変換（XSS対策）
        content: StringUtil.escapeHtml(content).replaceAll('\n', '<br />'),
        disp_at: `${date}<br />${time}`,
      }
    },
    // 今のお知らせは一覧の先頭か？
    isFirstInformation: function () {
      let index = this.informationIds.findIndex(
        (id) => id == this.information.id
      )
      return index === 0
    },
    // 今のお知らせは一覧の最終か？
    isLastInformation: function () {
      let lastIndex = this.informationIds.lastIndexOf(this.information.id)
      return lastIndex === this.informationIds.length - 1
    },
    // モーダルのOKボタンのvariant
    okButtonVariant: function () {
      return this.isFirstInformation ? 'secondary' : 'success'
    },
    // モーダルのCancelボタンのvariant
    cancelButtonVariant: function () {
      return this.isLastInformation ? 'secondary' : 'primary'
    },
  },
  mounted: function () {
    // 描画更新
    this.$_showInformations()
  },
  watch: {
    // 表示日数が変更されたときの処理
    days: function () {
      this.currentPage = 1 // ページを先頭に戻す
      this.$_showInformations()
    },
    // pagenationにアクションがあったときの処理
    currentPage: function () {
      this.$_showInformations()
    },
  },
  methods: {
    /**
     *  お知らせ一覧表示ラッパー
     */
    $_showInformations: function () {
      this.$_showInformationListLoading() // hideは$_showInformationList内で実行される
      this.$_setPeriod()
      this.$_showInformationList()
      this.$_setTotalRows()
    },

    /**
     * お知らせ詳細モーダルを表示する
     */
    $_showModal: function (id) {
      // ローディング表示
      this.$_showInformationLoading()
      // お知らせ詳細取得
      this.$_showInformationDetail(id).then(() => {
        // ローディング表示終了
        this.$_hideInformationLoading()
      })
      // モーダル表示
      this.$bvModal.show('information-dialog')
    },

    /**
     * お知らせ詳細を前へめくる
     */
    $_turnPreviousInformationDetail: async function (bvModalEvt) {
      // ローディング表示
      this.$_showInformationLoading()
      // モーダルを閉じない
      bvModalEvt.preventDefault()
      // お知らせ詳細取得
      await this.$_showInformationDetail(this.information.id, 1)
      // ローディング表示終了
      this.$_hideInformationLoading()
    },

    /**
     * お知らせ詳細を次へめくる
     */
    $_turnNextInformationDetail: async function (bvModalEvt) {
      // ローディング表示
      this.$_showInformationLoading()
      // モーダルを閉じない
      bvModalEvt.preventDefault()
      // お知らせ詳細取得
      await this.$_showInformationDetail(this.information.id, -1)
      // ローディング表示終了
      this.$_hideInformationLoading()
    },

    /**
     * お知らせ取得期間を設定する
     */
    $_setPeriod: function () {
      let dt = new Date()
      let to = toYyyyMmDd(dt)
      if (this.days === -1) {
        // 全て表示の場合はtoだけ
        this.period = { to: toYyyyMmDd(dt) }
      } else {
        let from = toYyyyMmDd(new Date(dt.setDate(dt.getDate() - this.days)))
        this.period = { from: from, to: to }
      }
    },

    /**
     * informations配列からIDを取得する
     * @param {int} id  元になるID
     * @param {int} offset  idに対するinformationsのoffset
     * @returns {int} 対象ID
     */
    $_getIDFromInformtaions: function (id, offset) {
      let idx = this.informationIds.indexOf(id)
      // informationsの0～最大番地に頭打ちする
      let resID = Math.max(
        0,
        Math.min(idx + offset, this.informationIds.length - 1)
      )
      return this.informationIds[resID]
    },

    /**
     *  お知らせ一覧を取得して表示する
     */
    $_showInformationList: async function () {
      // APIのURLを作成する
      let url = RestApiClient.createApiUrl('information')
      let params = url.searchParams
      // from,to は日付のみのためタイムゾーン変換不要
      if (Object.keys(this.period).includes('from')) {
        params.append('from', this.period.from)
      }
      params.append('to', this.period.to)
      params.append('limit', this.limit)
      let offset = (this.currentPage - 1) * this.limit
      params.append('offset', offset)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.informations = json.data.map((d) => ({
          ...d,
          disp_at: toLocalTz(d.disp_at),
        }))
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideInformationListLoading()
      }
    },

    /**
     *  お知らせの総件数を設定する
     */
    $_setTotalRows: async function () {
      // APIのURLを作成する
      let url = RestApiClient.createApiUrl('information/count')
      let params = url.searchParams
      // from,to は日付のみのためタイムゾーン変換不要
      if (Object.keys(this.period).includes('from')) {
        params.append('from', this.period.from)
      }
      params.append('to', this.period.to)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.totalRows = json.data.count
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * お知らせ詳細表示ラッパー
     * @param {int} id  お知らせ対象ID
     * @param {int} offset  idに対するinformationsのoffset
     * @returns {Promise}
     */
    $_showInformationDetail: async function (id, offset = 0) {
      let infosID = this.$_getIDFromInformtaions(id, offset)
      return this.$_getInformation(infosID)
    },

    /**
     * お知らせ詳細を取得して表示する
     * @param {int} id  対象お知らせID
     */
    $_getInformation: async function (id) {
      // APIのURLを作成する
      let url = RestApiClient.createApiUrl(`information/${id}`)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.information = {
          ...json.data,
          disp_at: toLocalTz(json.data.disp_at),
        }
      } catch (error) {
        this.handleApiError(error)
      }
    },

    /**
     * お知らせ一覧のローディングを表示する
     */
    $_showInformationListLoading: function () {
      this.isLoadingInformationList = true
    },

    /**
     * お知らせ一覧のローディングを非表示にする
     */
    $_hideInformationListLoading: function () {
      this.isLoadingInformationList = false
    },

    /**
     * お知らせ詳細のローディングを表示する
     */
    $_showInformationLoading: function () {
      this.isLoadingInformation = true
    },

    /**
     * お知らせ詳細のローディングを非表示にする
     */
    $_hideInformationLoading: function () {
      this.isLoadingInformation = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../assets/scss/parts/title-bar';

$-title-bar-height: 3.2rem;

.information-wrapper {
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #ebebeb;
  border-radius: 4px;
}

.title-bar {
  height: $-title-bar-height;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: $font-size-middle;
    letter-spacing: 0;
  }
}

.information-period-select {
  font-size: 0.8em;
  color: #959595;
  border: 0;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url('../assets/ifield/img_arrow_down.png');
  background-size: 24px 24px;
  background-position: right 2px center;
  margin: -7px -7px -7px 10px;
  cursor: pointer;
}

.information-body {
  padding: 20px 20px 6px 20px;
  height: calc(100% - #{$-title-bar-height});
}

.information-list {
  width: 100%;
  height: calc(100% - #{$pagination-height} - 10px);
  padding: 0;
  margin: 0 0 10px;
  box-sizing: border-box;
  list-style-type: none;
  background-color: #f6f6f6;
  overflow-y: auto;
  @include mixin-scrollbar;
}

.information-item {
  padding: 0 20px;
  border-bottom: 1px dashed #e2e2e2;

  &:last-child {
    border-bottom: none;
  }

  a {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 0;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      .information-title {
        text-decoration: underline;
      }
    }
  }
}

.information-title {
  margin: 0 10px 0 0;
  color: $text-color-link;
  font-size: 0.8em;
  font-weight: bold;
  text-align: left;
}

.information-date {
  margin: 0;
  color: #959595;
  font-size: 0.8em;
}

.information-pagination {
  @include mixin-pagination;
}

.dialog-body {
  overflow: hidden;
}

.dialog-body-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  background-color: #ffffff;
  border: 1px solid #f0f0f0;
  margin: 0;

  .dialog-logo {
    display: block;
    width: 120px;
    height: 40px;
    background: url('../assets/ifield/iFieldlogo.png') left center no-repeat;
    background-size: 120px 40px;
  }

  .diglog-content-date {
    display: block;
    width: auto;
    height: 40px;
    color: #959595;
    font-size: 0.8em;
    line-height: 20px;
    text-align: right;
    margin: 2px 0;
  }
}

.dialog-body-content {
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  border: 1px solid #f0f0f0;
  background-color: #f0f0f0;
  overflow: hidden;
  text-align: left;

  .dialog-content-title {
    width: calc(
      100% - 10px
    ); /* contentにスクロールバーが出た時にバーの上にタイトルが描画されるのを防ぐ用 */
    font-size: 1em;
    margin: 0 0 20px;
  }

  .dialog-content-text {
    font-size: 0.8em;
    margin: 0 0 20px;
    overflow-y: auto;
  }
}

.dialog-footer {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-footer-left {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  a {
    color: $text-color;
    text-decoration: none;
    margin: 0 10px 0 0;

    &:hover {
      color: $text-color;
      text-decoration: underline;
    }
  }
}

.dialog-footer-right {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
}
</style>
